import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { getContinent, filterVideosByRegion } from "../lib/regions";
import CosmicBackdrop from "../components/CosmicBackdrop"; // eslint-disable-line no-unused-vars
import XListsCTA from "../components/XListsCTA"; // eslint-disable-line no-unused-vars
// FeedPlayerOverlay no longer rendered here — moved to PlayerLayout
// via PersistentOverlay as part of the Iter-45 state lift.
import FirstTimeBookmarkTooltip from "../components/feed/FirstTimeBookmarkTooltip";
import { usePlayer } from "../context/PlayerContext";
import { ProfileTVHeader } from "./profile/ProfileTVHeader";
import { ProfileCreatorInfoBar } from "./profile/ProfileCreatorInfoBar";
import { ProfileSponsors } from "./profile/ProfileSponsors";
import JoinHereBox from "../components/shared/JoinHereBox";
import { useProfileData } from "./profile/useProfileData";
import { useProfileVideoSync } from "./profile/useProfileVideoSync";
import { ProfileLoadingError } from "./profile/ProfileLoadingError";

const ProfilePage = () => {
  const { username } = useParams();
  const [searchParams] = useSearchParams();
  const requestedVideoId = searchParams.get('video');
  const [followLoading, setFollowLoading] = useState(false);
  // Playback state lifted into PlayerContext (Iter-45). Shared with
  // FeedPage so the <video> DOM element + overlay survive navigation
  // between / and /:username.
  const {
    videoRef, userPausedRef, failedIdsRef, exhaustedToastShownRef,
    isPlaying, setIsPlaying,
    currentVideoIndex, setCurrentVideoIndex,
    setBundle,
  } = usePlayer();
  const [isLoopMode, setIsLoopMode] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);
  const [newChannelChat, setNewChannelChat] = useState("");
  const [submittingChannelChat, setSubmittingChannelChat] = useState(false);
  const [applauding, setApplauding] = useState(false);
  const [activeRegion, setActiveRegion] = useState('Global');
  const { user, token, updateCredits } = useAuth();
  // (videoRef, userPausedRef, failedIdsRef, exhaustedToastShownRef all
  // come from PlayerContext above. No local duplicates.)
  const navigate = useNavigate();

  // Trail (Save-to-Trail) IDs — hydrated on mount for logged-in users.
  // Matches FeedPage's behaviour so the bookmark icon paints correctly
  // on the profile player.
  const [trailIds, setTrailIds] = useState(() => new Set());
  // Sign-in prompt overlay state (FeedPlayerOverlay expects these).
  const [showSignInPrompt, setShowSignInPrompt] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [showCreditsPrompt, setShowCreditsPrompt] = useState(null);
  // Following lookup — empty {} on profile; FeedNowPlayingPanel handles
  // the absence gracefully (no Follow pill rendered for own producer).
  const [followingUsers] = useState({});
  // Track DISTINCT video IDs the user has viewed this session — used
  // by FirstTimeBookmarkTooltip after 3 views. (Lifted into the
  // useProfileVideoSync hook alongside the comments + nearby effects.)
  // Live-chat TTL config; matches FeedPage's default.
  const [liveChatTtlDays] = useState(30);

  // Reserved sub-paths that must never be treated as a username — these are
  // real routes elsewhere in the app and would 404 if we let them fall through
  // to /api/users/<reserved>. Computed once; set determines the early-bail flag.
  const RESERVED_USERNAMES = React.useMemo(() => new Set([
    'login', 'register', 'logout', 'admin', 'commissions', 'feed', 'map', 'pricing', 'video', 'dashboard',
    'forgot-password', 'reset-password', 'security',
  ]), []);
  const isReservedSlug = !!(username && RESERVED_USERNAMES.has(username.toLowerCase()));

  // Data fetches (profile + highlighted + featured + channel chat) are
  // bundled into a single hook. The playback-coupled effects (comments,
  // nearby, requested-video resolution) stay inline below since they
  // need to mutate PlayerContext state.
  const {
    profile,
    loading,
    highlightedVideos,
    followersCount, setFollowersCount,
    following, setFollowing,
    featuredVideos,
    channelChat, setChannelChat,
  } = useProfileData({ username, token, isReservedSlug });

  // Calculate playable videos — Iter-58.7 (2026-06-02) bugfix:
  // previously this returned `highlightedVideos` as soon as ANY were
  // present, which meant brand-new uploads were invisible on the
  // user's own profile (seeded videos auto-highlight on signup, so
  // the highlighted bucket was never empty). New behaviour: union
  // highlighted FIRST (so the user's chosen showcase still leads),
  // then append the rest of the channel's videos that aren't already
  // in the highlighted bucket. Deduped by id.
  const allPlayableVideos = React.useMemo(() => {
    if (!profile) return [];
    const profileVideos = profile.videos || [];
    if (highlightedVideos.length === 0) return profileVideos;
    const highlightedIds = new Set(highlightedVideos.map((v) => v.id));
    const rest = profileVideos.filter((v) => !highlightedIds.has(v.id));
    return [...highlightedVideos, ...rest];
  }, [highlightedVideos, profile]);

  const profileRegionCounts = React.useMemo(() => {
    const counts = { Global: allPlayableVideos.length, Americas: 0, Asia: 0, Africa: 0, Europe: 0, Oceania: 0 };
    allPlayableVideos.forEach(v => {
      const c = getContinent(v?.location);
      if (counts[c] !== undefined) counts[c] += 1;
    });
    return counts;
  }, [allPlayableVideos]);

  const playableVideos = React.useMemo(
    () => filterVideosByRegion(allPlayableVideos, activeRegion),
    [allPlayableVideos, activeRegion]
  );

  // Reset playback index when region filter trims the list below the current index
  React.useEffect(() => {
    if (currentVideoIndex >= playableVideos.length) {
      setCurrentVideoIndex(0);
    }
  }, [activeRegion, playableVideos.length]);

  // Handle requested video - find its index and auto-start playback
  useEffect(() => {
    if (requestedVideoId && playableVideos.length > 0 && !isPlaying) {
      const videoIndex = playableVideos.findIndex(v => v.id === requestedVideoId);
      if (videoIndex !== -1) {
        setCurrentVideoIndex(videoIndex);
        setIsPlaying(true);
      }
    }
  }, [requestedVideoId, playableVideos]);

  // Comments + nearby-videos + viewed-set side-effects bundled into a
  // single hook to keep this file under the 600-line product rule.
  const {
    comments, setComments,
    loadingComments,
    viewedVideoIds,
  } = useProfileVideoSync({ isPlaying, currentVideoIndex, playableVideos });

  // Hydrate Save-to-Trail IDs on mount. Must live BEFORE the early-
  // return guards (loading/!profile/isReservedSlug) so the hook order
  // stays stable across renders.
  useEffect(() => {
    if (!user || !token) { setTrailIds(new Set()); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/users/me/trail/ids`);
        if (!cancelled) setTrailIds(new Set(res.data?.ids || []));
      } catch (err) {
        // Non-fatal — trail ids power "already watched" badges only.
        // Log so a sustained API outage is visible in dev console, but
        // never bubble to the user (their profile still renders).
        console.warn("Failed to load watch-trail ids:", err);
      }
    })();
    return () => { cancelled = true; };
  }, [user, token]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!user || !newComment.trim() || !playableVideos[currentVideoIndex]) return;
    
    setSubmittingComment(true);
    try {
      const res = await api.post(`/videos/${playableVideos[currentVideoIndex].id}/comments`,
        { content: newComment }
      );
      setComments([res.data.comment || res.data, ...comments]);
      setNewComment("");
      toast.success("Comment added!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add comment");
    }
    setSubmittingComment(false);
  };
  // Reply to an existing comment on the active profile video. Mirrors
  // useFeedActions.handleSubmitReply so the threaded UI works the same
  // way on profile pages as on the home feed.
  const handleSubmitReply = async (parentId, replyText) => {
    const text = (replyText || "").trim();
    if (!user || !text || !parentId || !playableVideos[currentVideoIndex]) {
      return { ok: false };
    }
    try {
      const res = await api.post(
        `/videos/${playableVideos[currentVideoIndex].id}/comments`,
        { content: text, parent_id: parentId },
      );
      const reply = res.data.comment || res.data;
      setComments(prev => prev.map(c => (
        c.id === parentId
          ? { ...c, replies: [...(c.replies || []), reply] }
          : c
      )));
      toast.success("Reply added!");
      return { ok: true };
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reply");
      return { ok: false };
    }
  };
  const handleSubmitChannelChat = async (e) => {
    e.preventDefault();
    if (!user || !newChannelChat.trim()) return;
    
    setSubmittingChannelChat(true);
    try {
      const res = await api.post(`/users/${profile.username}/channel-chat`,
        { content: newChannelChat }
      );
      setChannelChat([...channelChat, res.data]);
      setNewChannelChat("");
      toast.success("Message sent!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send message");
    }
    setSubmittingChannelChat(false);
  };

  const handleApplaudVideo = async () => {
    if (!user) {
      toast.error("Please sign in to applaud videos");
      return;
    }
    const currentVideo = playableVideos[currentVideoIndex];
    if (!currentVideo) return;
    if (currentVideo.user_id === user.id) {
      toast.error("You can't applaud your own video");
      return;
    }
    if (user.credits < 0.50) {
      toast.error("Insufficient credits (need 0.50)");
      return;
    }
    
    setApplauding(true);
    try {
      const res = await api.post(`/videos/${currentVideo.id}/applaud`, {});
      toast.success("Applauded! 👏");
      updateCredits(res.data.new_balance);
      // Update local video applaud count
      const updatedVideos = [...playableVideos];
      updatedVideos[currentVideoIndex] = {
        ...updatedVideos[currentVideoIndex],
        applaud_count: (updatedVideos[currentVideoIndex].applaud_count || 0) + 1
      };
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to applaud");
    }
    setApplauding(false);
  };

  const handleFollow = async () => {
    if (!user) {
      toast.error("Please sign in to follow users");
      return;
    }
    setFollowLoading(true);
    try {
      if (following) {
        const res = await api.delete(`/users/${username}/follow`);
        setFollowing(false);
        setFollowersCount(res.data.followers_count);
        toast.success(`Unfollowed @${username}`);
      } else {
        const res = await api.post(`/users/${username}/follow`, {});
        setFollowing(true);
        setFollowersCount(res.data.followers_count);
        toast.success(`Now following @${username}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update follow");
    }
    setFollowLoading(false);
  };

  // (Early-return guards moved DOWN to after all hooks — see comment
  // near the `return (` below. React requires hooks be called in the
  // same order every render, so the useEffect that pushes the player
  // bundle to PlayerContext must run even on renders where profile is
  // still loading or null.)

  const isOwnProfile = user && user.username === profile?.username;
  const handlePlayAll = () => {
    if (playableVideos.length > 0) {
      setCurrentVideoIndex(0);
      setIsPlaying(true);
    } else if (activeRegion !== 'Global') {
      toast.error(`No videos in ${activeRegion} for this channel`);
    }
  };

  const handleVideoEnded = () => {
    if (isLoopMode) {
      // In loop mode, replay the current video
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        const p = videoRef.current.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      }
    } else {
      // In playlist mode, go to next video
      if (currentVideoIndex < playableVideos.length - 1) {
        setCurrentVideoIndex(currentVideoIndex + 1);
      } else {
        setCurrentVideoIndex(0); // Loop back to start
      }
    }
  };

  // Programmatic play on index change — mirror of FeedPage's effect.
  // The shared FeedVideoPlayer renders the <video> without an autoPlay
  // attribute (iOS WKWebView's autoPlay was the source of the "pause
  // doesn't pause" bug), so each surface that hosts the player has to
  // explicitly kick playback when the source video changes. Without
  // this effect, `handleVideoEnded` advancing the index would land the
  // viewer on a new (paused) video with the giant Play icon showing.
  // Gated on userPausedRef so a manual pause is never overridden by
  // a downstream re-render.
  //
  // For loop mode the index doesn't change → this effect doesn't fire
  // → the in-handler `videoRef.current.play()` above is what restarts
  // the clip. Both paths are intentionally separate.
  React.useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (!isPlaying) return;
    if (userPausedRef.current) return;
    const promise = v.play();
    if (promise && typeof promise.catch === 'function') {
      promise.catch(() => { /* user-initiated pause / iOS gesture lock */ });
    }
  }, [currentVideoIndex, isPlaying, videoRef, userPausedRef]);

  const handleClosePlayer = () => {
    setIsPlaying(false);
    setCurrentVideoIndex(0);
    setActiveRegion('Global');
  };

  const handleSkipNext = () => {
    if (currentVideoIndex < playableVideos.length - 1) {
      setCurrentVideoIndex(currentVideoIndex + 1);
    } else {
      setCurrentVideoIndex(0);
    }
  };

  // Helpers required by the shared FeedPlayerOverlay's auto-skip
  // machinery. Returns the next index whose video isn't in the
  // failedIdsRef set, or null if all remaining videos have failed.
  const findNextPlayableIndex = (fromIndex) => {
    for (let offset = 1; offset <= playableVideos.length; offset++) {
      const idx = (fromIndex + offset) % playableVideos.length;
      const v = playableVideos[idx];
      if (v && !failedIdsRef.current.has(v.id)) return idx;
    }
    return null;
  };

  // Native share — uses navigator.share when available (mobile),
  // falls back to clipboard.
  const handleShare = async () => {
    const v = playableVideos[currentVideoIndex];
    if (!v) return;
    const url = `${window.location.origin}/${profile.username}?video=${v.id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: v.title, text: `Watch this on GlobalEyes.TV`, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied!");
      }
    } catch (err) { /* user-cancelled */ }
  };

  // Channel chat aliased as homeChat for the shared overlay. Same data,
  // different prop name — we adapt on the boundary instead of forking
  // the component.
  const handleSubmitHomeChat = handleSubmitChannelChat;
  const handleDeleteHomeChat = async (msgId) => {
    if (!user || !msgId) return;
    try {
      await api.delete(`/users/${profile.username}/channel-chat/${msgId}`);
      setChannelChat(channelChat.filter(m => m.id !== msgId));
    } catch (err) {
      toast.error("Couldn't delete message");
    }
  };

  // FeedPlayerOverlay calls this when the user taps the follow pill
  // inside the Now Playing panel. On a profile we just delegate to the
  // existing handleFollow (the producer is always this profile).
  const handleFollowFromFeed = () => { handleFollow(); };

  // Save-to-Trail toggle — mirrors FeedPage's optimistic flow.
  const handleToggleTrail = async () => {
    if (!user) { setShowSignInPrompt(true); setPendingAction('trail'); return; }
    const v = playableVideos[currentVideoIndex];
    if (!v) return;
    const wasSaved = trailIds.has(v.id);
    setTrailIds(prev => {
      const next = new Set(prev);
      if (wasSaved) next.delete(v.id); else next.add(v.id);
      return next;
    });
    try {
      const res = await api.post(`/videos/${v.id}/trail`, {});
      setTrailIds(prev => {
        const next = new Set(prev);
        if (res.data?.saved) next.add(v.id); else next.delete(v.id);
        return next;
      });
      toast.success(res.data?.saved ? "Saved to your Trail" : "Removed from your Trail");
    } catch (err) {
      setTrailIds(prev => {
        const next = new Set(prev);
        if (wasSaved) next.add(v.id); else next.delete(v.id);
        return next;
      });
      toast.error("Couldn't update Trail");
    }
  };

  // Credits-prompt config required by FeedPrompts.
  const creditsPromptConfig = {
    comment: { amount: 0.20, action: 'post a comment' },
    applaud: { amount: 0.50, action: 'applaud this video' },
    homeChat: { amount: 0.20, action: 'post a live-chat message' },
  };

  // Push the profile-specific bundle into PlayerContext so the shared
  // PersistentOverlay renders the player on this page. Only runs while
  // `isPlaying` is true — see FeedPage's matching effect for the
  // rationale (infinite-loop guard).
  React.useEffect(() => {
    if (!isPlaying || playableVideos.length === 0) {
      setBundle(null);
      return;
    }
    setBundle({
      mode: 'profile',
      onCloseOverlay: handleClosePlayer,
      activeVideos: playableVideos,
      videos: playableVideos,
      isLoopMode, setIsLoopMode,
      source: 'all',
      setSource: () => {},
      setIsPlaying,
      activeRegion, setActiveRegion,
      regionCounts: {},
      findNextPlayableIndex,
      handleClosePlayer,
      handleVideoEnded,
      handleSkipNext,
      handleShare,
      handleApplaud: handleApplaudVideo,
      handleFollowFromFeed,
      handleSubmitComment,
      handleSubmitReply,
      handleSubmitHomeChat,
      handleDeleteHomeChat,
      comments,
      loadingComments,
      newComment, setNewComment,
      submittingComment,
      homeChat: channelChat,
      newHomeChat: newChannelChat,
      setNewHomeChat: setNewChannelChat,
      submittingHomeChat: submittingChannelChat,
      liveChatTtlDays,
      featuredVideos,
      followingUsers,
      trailIds,
      handleToggleTrail,
      showSignInPrompt, setShowSignInPrompt,
      pendingAction, setPendingAction,
      showCreditsPrompt, setShowCreditsPrompt,
      creditsPromptConfig,
      user,
      navigate,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isPlaying, playableVideos, isLoopMode, activeRegion,
    comments, loadingComments, newComment, submittingComment,
    channelChat, newChannelChat, submittingChannelChat,
    featuredVideos, trailIds, showSignInPrompt, pendingAction,
    showCreditsPrompt, user,
  ]);

  // Clear the bundle on unmount (route change away from profile).
  React.useEffect(() => {
    return () => setBundle(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // EARLY-RETURN GUARDS — placed AFTER all hooks per React's
  // hook-order invariant. The 3 conditions surface route states where
  // the main JSX shouldn't render: reserved slug, loading, or failed
  // profile fetch.
  if (isReservedSlug) return <Navigate to="/" replace />;
  if (loading) return <ProfileLoadingError state="loading" />;
  if (!profile) return <ProfileLoadingError state="notfound" onHome={() => navigate('/')} />;

  return (
    <div
      // Jun 9, 2026 — Eric's mobile-prod review: the landing state used
      // `h-[calc(100dvh-4rem)] overflow-hidden` which clipped the
      // JoinHereBox (and the ProfileSponsors row beneath it) below the
      // fold on iPhone-sized viewports. The locked layout was meant to
      // pin the TV header in view on desktop, but it shouldn't trap
      // mobile users above the conversion CTA. On <lg widths we now
      // allow native scroll; desktop keeps the locked-fold behaviour.
      //
      // Jun 10, 2026 followup — mobile pages also need a generous
      // `pb-24` so the iOS Safari URL/share/tabs toolbar (~80-100pt
      // above the home indicator) can't cover the JoinHereBox /
      // sponsors at the bottom of the page. `env(safe-area-inset-
      // bottom)` alone is insufficient — it only clears the home
      // indicator.
      className={`w-full bg-gradient-to-b from-gray-950 to-black relative pb-24 lg:pb-0 ${isPlaying ? 'min-h-screen' : 'lg:h-[calc(100dvh-4rem)] lg:overflow-hidden'}`}
      data-testid="profile-page"
    >
      <div className="relative z-10 h-full flex flex-col">
      {/* Video Player with Branding */}
      {/* PROFILE PLAYER (Iter-45): no longer renders FeedPlayerOverlay
          locally. PlayerLayout's PersistentOverlay renders it from the
          bundle pushed below via setBundle(). This makes the <video>
          DOM node survive /:username ↔ / route changes — playback no
          longer resets when the user navigates between surfaces.

          PROP ALIASING (Iter-42 refactor, still applies): ProfilePage's
          own state shape (channelChat / handleSubmitChannelChat) is
          mapped to the shared overlay's prop names (homeChat /
          handleSubmitHomeChat) inside the bundle below. */}
      {isPlaying && playableVideos.length > 0 && (
        <FirstTimeBookmarkTooltip videoViews={viewedVideoIds.size} user={user} />
      )}

      {/* TV Station Header — when locked (landing state), the inner
          container collapses padding and flex-fills so the TV+info
          block centers in the viewport. */}
      <div className={`relative overflow-hidden ${isPlaying ? '' : 'flex-1 flex items-center'}`}>
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/30 via-gray-900 to-purple-900/30" />

        <div className={`relative container mx-auto px-4 ${isPlaying ? 'py-6 sm:py-8' : 'py-4 sm:py-6 lg:max-h-full lg:overflow-hidden'}`}>
          <ProfileTVHeader
            profile={profile}
            playableVideos={playableVideos}
            activeRegion={activeRegion}
            onPlayAll={handlePlayAll}
          />

          <ProfileCreatorInfoBar
            profile={profile}
            followersCount={followersCount}
            following={following}
            followLoading={followLoading}
            onFollow={handleFollow}
            isOwnProfile={isOwnProfile}
            user={user}
          />

          {/* Profile-as-referral-link hero card. Renders only on the
              landing (non-playing) state so the URL is the loudest thing
              on screen when a visitor arrives. Auto-hides for signed-in
              non-owners (they're already in). */}
          {!isPlaying && (
            <JoinHereBox profileUsername={profile.username} variant="hero" />
          )}

          <ProfileSponsors links={profile.affiliate_links} />
        </div>
      </div>

      {/* Featured Broadcasts section removed from public/landing profile per design.
          Profile owners can still manage these from the Dashboard.  */}
      </div>
    </div>
  );
};

export default ProfilePage;
